import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { Tables, TablesUpdate } from "@/integrations/supabase/types";

export type SprintTier = "self_service" | "with_call";
export type SprintStatus = "pending_payment" | "active" | "completed" | "expired" | "cancelled";
export type RealityCheckMode = "linked" | "virtual" | "none";

export interface V2Sprint {
  id: string;
  user_id: string;
  source_reality_check_id: string | null;
  tier: SprintTier;
  status: SprintStatus;
  current_step: number;
  completed_steps: number[];
  started_at: string | null;
  expires_at: string | null;
  completed_at: string | null;
  last_submission_at: string | null;
  call_unlocked_at: string | null;
  call_booked_at: string | null;
  extension_count: number;
  created_at: string;
}

export interface V2SprintSubmission {
  id: string;
  sprint_id: string;
  user_id: string;
  step_number: number;
  content: Record<string, any>;
  evidence_links: string[];
  evidence_files: any[];
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

export type V2RealityCheck = Pick<
  Tables<"v2_reality_checks">,
  | "id"
  | "idea_summary"
  | "target_summary"
  | "main_risk"
  | "verdict"
  | "benchmark_label"
  | "percentile"
  | "experiment_48h"
  | "success_metric"
  | "minimum_success_threshold"
  | "cta_angle"
  | "why_this_experiment"
  | "test_channel"
>;

const SPRINT_KEY = (uid?: string) => ["v2-sprint", uid];
const SUBS_KEY = (sid?: string) => ["v2-sprint-submissions", sid];

export function useV2Sprint() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const sprintQuery = useQuery({
    queryKey: SPRINT_KEY(user?.id),
    enabled: !!user?.id,
    queryFn: async () => fallbackLoad(user!.id),
  });

  const sprint = sprintQuery.data as
    | (V2Sprint & {
        reality_check: V2RealityCheck | null;
        reality_check_mode: RealityCheckMode;
      })
    | null;

  const submissionsQuery = useQuery({
    queryKey: SUBS_KEY(sprint?.id),
    enabled: !!sprint?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v2_sprint_submissions")
        .select("*")
        .eq("sprint_id", sprint!.id)
        .order("step_number", { ascending: true });
      if (error) throw error;
      return (data ?? []) as V2SprintSubmission[];
    },
  });

  const submissions = submissionsQuery.data ?? [];

  const submitStep = useMutation({
    mutationFn: async (args: {
      stepNumber: number;
      content: Record<string, any>;
      evidenceLinks?: string[];
      /** Default true: marks the step completed and advances current_step.
       *  Pass false for draft / progress saves that must not advance the sprint. */
      completeStep?: boolean;
      /** Only meaningful for tier="with_call". Unlocks the call when the user
       *  transitions Step 3 from planning to running. Existing call_unlocked_at
       *  is never overwritten. */
      unlockCall?: boolean;
    }) => {
      if (!sprint || !user) throw new Error("Sprint non disponibile");
      if (sprint.status !== "active") throw new Error("Sprint non più attivo");

      const { stepNumber, content, evidenceLinks } = args;
      const completeStep = args.completeStep ?? true;
      const unlockCall = args.unlockCall ?? false;

      const existing = submissions.find((s) => s.step_number === stepNumber);
      const now = new Date().toISOString();

      // Merge against the freshest server-side content to avoid stale
      // overwrites (e.g. two tabs, retried mutations). Fall back silently
      // if the fetch fails — local cache content is still merged below.
      let freshContent: Record<string, any> = (existing?.content as any) ?? {};
      let freshLinks: string[] = existing?.evidence_links ?? [];
      if (existing?.id) {
        const { data: latest } = await supabase
          .from("v2_sprint_submissions")
          .select("content, evidence_links")
          .eq("id", existing.id)
          .maybeSingle();
        if (latest) {
          freshContent = (latest.content as any) ?? freshContent;
          freshLinks = (latest.evidence_links as any) ?? freshLinks;
        }
      }
      const mergedContent = { ...freshContent, ...content };
      const mergedLinks = evidenceLinks ?? freshLinks;

      const { error: subErr } = await supabase.from("v2_sprint_submissions").upsert(
        {
          ...(existing ? { id: existing.id } : {}),
          sprint_id: sprint.id,
          user_id: user.id,
          step_number: stepNumber,
          content: mergedContent,
          evidence_links: mergedLinks,
          submitted_at: now,
          updated_at: now,
        },
        { onConflict: "id" }
      );
      if (subErr) throw subErr;

      // Draft / progress save: never advance the sprint. Optionally unlock
      // the call when the user explicitly transitions planning → running
      // on Step 3 (tier with_call only, never overwrite).
      if (!completeStep) {
        if (
          unlockCall &&
          stepNumber === 2 &&
          sprint.tier === "with_call" &&
          !sprint.call_unlocked_at
        ) {
          const { error: cErr } = await supabase
            .from("v2_sprints")
            .update({ call_unlocked_at: now, updated_at: now })
            .eq("id", sprint.id);
          if (cErr) throw cErr;
        }
        return {
          editOnly: false,
          completed: false,
          nextStep: null as number | null,
          draftOnly: true,
        };
      }

      const wasCompleted = sprint.completed_steps.includes(stepNumber);
      const isEditOnly = !!existing && wasCompleted;

      if (isEditOnly) {
        return { editOnly: true, completed: false, nextStep: null as number | null, draftOnly: false };
      }

      const nextCompleted = Array.from(
        new Set([...(sprint.completed_steps ?? []), stepNumber])
      ).sort((a, b) => a - b);
      const isFinal = stepNumber === 5;
      const nextCurrent = isFinal ? 5 : Math.min(5, Math.max(sprint.current_step, stepNumber + 1));

      const update: TablesUpdate<"v2_sprints"> = {
        completed_steps: nextCompleted,
        current_step: nextCurrent,
        last_submission_at: now,
        updated_at: now,
      };

      if (isFinal) {
        update.status = "completed";
        update.completed_at = now;
      }

      if (
        stepNumber === 2 &&
        sprint.tier === "with_call" &&
        !sprint.call_unlocked_at
      ) {
        update.call_unlocked_at = now;
      }

      const { error: sErr } = await supabase
        .from("v2_sprints")
        .update(update)
        .eq("id", sprint.id);
      if (sErr) throw sErr;

      return {
        editOnly: false,
        completed: isFinal,
        nextStep: isFinal ? null : nextCurrent,
        draftOnly: false,
      };
    },
    onMutate: async () => {
      // Evita che un refetch in volo sovrascriva l'ottimistico.
      await qc.cancelQueries({ queryKey: SPRINT_KEY(user?.id) });
    },
    onSuccess: (res, vars) => {
      // Optimistic cache update so UI gates (based on completed_steps)
      // don't show a stale "locked" state while the refetch is in flight.
      if (res && !res.editOnly && !res.draftOnly && sprint) {
        qc.setQueryData(SPRINT_KEY(user?.id), (prev: any) => {
          if (!prev) return prev;
          const nextCompleted = Array.from(
            new Set([...(prev.completed_steps ?? []), vars.stepNumber])
          ).sort((a: number, b: number) => a - b);
          const nextCurrent = vars.stepNumber === 5
            ? 5
            : Math.min(5, Math.max(prev.current_step ?? 1, vars.stepNumber + 1));
          return {
            ...prev,
            completed_steps: nextCompleted,
            current_step: nextCurrent,
            ...(res.completed ? { status: "completed" } : {}),
          };
        });
      }
      // Submissions: refetch subito (non impatta sui gate).
      qc.invalidateQueries({ queryKey: SUBS_KEY(sprint?.id) });
      // Sprint: differisci il refetch per lasciare visibile l'ottimistico ed
      // evitare race con il render successivo a setActiveStep.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: SPRINT_KEY(user?.id) });
      }, 600);
      // Toasts: draftOnly è muto (toast contestuali gestiti dal chiamante).
      if (res?.draftOnly) return;
      if (res?.completed) {
        toast.success("Sprint completato. Puoi consultare il lavoro fatto, ma non modificarlo.");
      } else if (res?.editOnly) {
        toast.success("Modifiche salvate.");
      } else {
        toast.success(`Step ${vars.stepNumber} completato. Ora passa allo Step ${res.nextStep}.`);
      }
    },
    onError: (e: any) => {
      toast.error(e?.message ?? "Errore nel salvataggio");
    },
  });

  const now = Date.now();
  const expiresAt = sprint?.expires_at ? new Date(sprint.expires_at).getTime() : null;
  const startedAt = sprint?.started_at ? new Date(sprint.started_at).getTime() : null;
  const hasExpiry = expiresAt != null;
  const isCompleted = sprint?.status === "completed";

  // Deadline dinamico: se Step 2 ha duration_days, usiamo started_at + duration_days + 3 (buffer analisi)
  const ANALYSIS_BUFFER_DAYS = 3;
  const step2Sub = submissions.find((s) => s.step_number === 2);
  const step2DurationDays =
    step2Sub && typeof (step2Sub.content as any)?.duration_days === "number"
      ? Number((step2Sub.content as any).duration_days)
      : typeof (step2Sub?.content as any)?.duration_days === "string" &&
        !isNaN(Number((step2Sub?.content as any)?.duration_days))
      ? Number((step2Sub!.content as any).duration_days)
      : 0;

  const effectiveTotalDays =
    step2DurationDays > 0 ? step2DurationDays + ANALYSIS_BUFFER_DAYS : 14;

  const effectiveExpiresAt =
    startedAt != null && step2DurationDays > 0
      ? startedAt + effectiveTotalDays * 24 * 60 * 60 * 1000
      : expiresAt;

  const totalDays = effectiveTotalDays;
  const daysRemaining =
    effectiveExpiresAt != null
      ? Math.max(0, Math.ceil((effectiveExpiresAt - now) / (1000 * 60 * 60 * 24)))
      : null;
  const dayNumber =
    startedAt != null
      ? Math.min(totalDays, Math.max(1, Math.ceil((now - startedAt) / (1000 * 60 * 60 * 24))))
      : null;

  // Aggiorna isExpired con la deadline effettiva (sovrascrive il check precedente)
  const isExpiredByDateEffective =
    effectiveExpiresAt != null && effectiveExpiresAt < now && !isCompleted;

  const isExpiredByDate = isExpiredByDateEffective ?? (!!expiresAt && expiresAt < now && !isCompleted);
  const isExpired = sprint?.status === "expired" || isExpiredByDate;
  const readOnly = isCompleted || isExpired;

  const completedCount = sprint?.completed_steps?.length ?? 0;
  const progressPct = Math.round((completedCount / 5) * 100);
  const currentStep = sprint?.current_step ?? 1;

  return {
    user,
    loading: sprintQuery.isLoading || (!!sprint && submissionsQuery.isLoading),
    sprint,
    realityCheck: sprint?.reality_check ?? null,
    realityCheckMode: (sprint?.reality_check_mode ?? "none") as RealityCheckMode,
    submissions,
    submitStep,
    readOnly,
    isCompleted,
    isExpired,
    hasExpiry,
    daysRemaining,
    dayNumber,
    totalDays,
    completedCount,
    progressPct,
    currentStep,
    refetch: () => {
      qc.invalidateQueries({ queryKey: SPRINT_KEY(user?.id) });
      qc.invalidateQueries({ queryKey: SUBS_KEY(sprint?.id) });
    },
  };
}

async function fallbackLoad(userId: string) {
  const { data, error } = await supabase
    .from("v2_sprints")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(20);
  if (error) throw error;
  if (!data || data.length === 0) return null;

  const chosen: any = data.find((s) => s.status === "active") ?? data[0];

  let reality_check: V2RealityCheck | null = null;
  let reality_check_mode: RealityCheckMode = "none";
  const rcSelect =
    "id, idea_summary, target_summary, main_risk, verdict, benchmark_label, percentile, experiment_48h, success_metric, minimum_success_threshold, cta_angle, why_this_experiment, test_channel";

  if (chosen.source_reality_check_id) {
    const { data: rc } = await supabase
      .from("v2_reality_checks")
      .select(rcSelect)
      .eq("id", chosen.source_reality_check_id)
      .maybeSingle();
    if (rc) {
      reality_check = rc;
      reality_check_mode = "linked";
    }
  }

  // Fallback: if no RC linked (or link points to missing row), pick the user's
  // most recent completed Reality Check so the Sprint UI is still contextual.
  if (!reality_check) {
    const { data: rc } = await supabase
      .from("v2_reality_checks")
      .select(rcSelect)
      .eq("user_id", userId)
      .eq("status", "completed")
      .order("completed_at", { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();
    if (rc) {
      reality_check = rc;
      reality_check_mode = "virtual";
    }
  }

  return normalizeSprint({ ...chosen, reality_check, reality_check_mode });
}

function normalizeSprint(
  row: any
): V2Sprint & { reality_check: V2RealityCheck | null; reality_check_mode: RealityCheckMode } {
  const parseCompletedSteps = (v: any): number[] => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.startsWith('{')) {
      return v.replace(/[{}]/g, '').split(',').map(Number).filter(n => !isNaN(n));
    }
    return [];
  };

  return {
    ...row,
    completed_steps: parseCompletedSteps(row.completed_steps),
    reality_check: row.reality_check ?? null,
    reality_check_mode: (row.reality_check_mode ?? "none") as RealityCheckMode,
  };
}
